import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import {
  addVenue,
  VenueCells,
  deleteVenue,
  editVenue,
  getVenues,
} from "framework/venue";
import { Feature } from "models/api";
import { IVenue, IVenueInput } from "models/venue";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import AddVenue from "./partials/AddVenue/AddVenue";
import EditVenue from "./partials/EditVenue/EditVenue";
import DeleteVenue from "./partials/DeleteVenue/DeleteVenue";
import Search from "components/Search/Search";
import { getCities } from "framework/cities";
import { ICity } from "models/city";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import BlockIcon from "@mui/icons-material/Block";
import { getPermission, getUserData } from "helpers";
import useFetchSection from "hooks/useFetchSection";
interface Props {
  feature: Feature;
}

const Venues: React.FC<Props> = ({ feature }) => {
  const [loading4, setLoading4] = useState<boolean>(true);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [Cities, setCities] = useState<ICity[]>([] as ICity[]);
  const { t, i18n } = useTranslation();
  const [FilterVenues, setFilterVenues] = useState<IVenue[]>([] as IVenue[]);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedVenue, setSelectedVenue] = useState<IVenue>({} as IVenue);
  const [inputObj, setInputObj] = useState<IVenueInput>({} as IVenueInput);
  const { setAlert } = useAlert();
  const user = getUserData();
  const { colors } = user;
  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addVenuesMutation = useMutation({
    mutationFn: (createInput: IVenueInput) => {
      return addVenue(createInput);
    },
  });

  const editVenuesMutation = useMutation({
    mutationFn: (data: { createInput: IVenueInput; id: string }) => {
      return editVenue(data.createInput, data.id);
    },
  });

  const deleteVenuesMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteVenue(id);
    },
  });

  const fetchCities = useCallback(async () => {
    setLoading4(true);
    try {
      let response: any = await queryClient.fetchQuery("get Cities", () =>
        getCities()
      );
      setCities(response);
      setLoading4(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading4(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const {
    loading,
    data: Venues,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getVenues(),
    fetchPermission: () => getPermission(5),
    section: "Venues",
    onSuccess: (data) => setFilterVenues(data),
  });

  useEffect(() => {
    fetchCities();

    // eslint-disable-next-line
  }, []);

  if (loading || loading4)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (Venue: IVenue) => {
    setSelectedVenue(Venue);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedVenue({} as IVenue);
  };

  const handleOpenDelete = (Venue: IVenue) => {
    setSelectedVenue(Venue);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedVenue({} as IVenue);
  };

  const getBodyCells = (row: IVenue) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "cityId")
        return {
          id: "cityId",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.cityName}</Typography>
            </TableCell>
          ),
        };
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "nameAr")
        return {
          id: "nameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.nameAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "latitude")
        return {
          id: "latitude",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.latitude}</Typography>
            </TableCell>
          ),
        };
      if (key === "longitude")
        return {
          id: "longitude",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.longitude}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
      else return <></>;
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.name || !inputObj.nameAr || !inputObj.cityId)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);
      inputObj.latitude = parseFloat(inputObj.latitude || (0 as any));
      inputObj.longitude = parseFloat(inputObj.longitude || (0 as any));
      await addVenuesMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Venue added successfully",
        type: "success",
      });
      revalidate();
      handleCloseAdd();
      setInputObj({} as IVenueInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    if (
      !inputObj.name &&
      !inputObj.cityId &&
      !inputObj.latitude &&
      !inputObj.nameAr &&
      !inputObj.longitude
    )
      return setAlert({
        open: true,
        message: "No changes detected",
        type: "error",
      });
    try {
      setLoading2(true);
      let createInput: IVenueInput = {
        name: inputObj.name || selectedVenue.name,
        cityId: inputObj.cityId || selectedVenue.cityId,
        latitude: inputObj.latitude || selectedVenue.latitude,
        longitude: inputObj.longitude || selectedVenue.longitude,
        nameAr: inputObj.nameAr || selectedVenue.nameAr,
      };
      await editVenuesMutation.mutateAsync({
        createInput,
        id: selectedVenue.id,
      });
      setAlert({
        open: true,
        message: "Venue updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setInputObj({} as IVenueInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteVenuesMutation.mutateAsync(selectedVenue.id);
      setAlert({
        open: true,
        message: "Venue deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterVenues(Venues!);
    let filtered = Venues!.filter((Venue) =>
      Venue.name.toLowerCase().includes(value.toLowerCase()) || Venue.nameAr.toLowerCase().includes(value.toLowerCase())
      || Venue?.cityName?.toLowerCase()?.includes(value.toLowerCase())
      || Venue.latitude?.toString()?.toLowerCase()?.includes(value.toLowerCase())
      || Venue.longitude?.toString()?.toLowerCase()?.includes(value.toLowerCase())
    );
    setFilterVenues(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1} justifyContent={"space-between"}>
            <Search handleChange={handleSearch} placeholder={t("T_venus")} />
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add_venue")}>
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterVenues.length > 0 && (
          <List
            headCells={VenueCells}
            data={FilterVenues}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterVenues || FilterVenues.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_venue_header")}
              message={t("T_no_venue_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddVenue
          open={openAdd}
          loading={loading2}
          cities={Cities}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditVenue
          open={openEdit}
          loading={loading2}
          Venue={selectedVenue}
          cities={Cities}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteVenue
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default Venues;
